import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Calendar,
  User,
  FolderOpen,
  Undo2,
  List,
} from "lucide-react";

import PageTitle from "../components/common/PageTitle";
import LoadingOverlay from "../components/common/LoadingOverlay";
import {
  EditButton,
  DeleteButton,
} from "../components/common/ActionButtons";

import {
  getLessonById,
  deleteLesson,
  getCategories,
} from "../services/lessonService";

import {
  showError,
  showSuccess,
} from "../utils/toast";

import {
  confirmDelete,
  showConfirm,
} from "../utils/confirm";

import { formatDateShort } from "../utils/dateFormat";

import StatusToggle from "../components/common/StatusToggle";

import { API_BASE_URL } from "../api/apiClient";

import {
  ROUTES,
  ROLES,
  JWT,
} from "../config/constants";

export default function LessonDetail() {
  const navigate = useNavigate();

  const { id } = useParams();

  const [lesson, setLesson] = useState(null);

  const [category, setCategory] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [selectedPhoto, setSelectedPhoto] =
    useState(null);

  const userRole = localStorage.getItem(
    JWT.ROLE_KEY,
  );

  const canApprove =
    userRole === ROLES.PROJECT_ADMIN;

  useEffect(() => {
    loadLesson();
    loadCategories();
  }, [id]);

  async function loadLesson() {
    if (!id) return;

    setLoading(true);

    try {
      const data = await getLessonById(id);

      setLesson(data.lesson || data);
    } catch (err) {
      console.error(
        "Failed to load lesson:",
        err,
      );

      showError("Gagal memuat data lesson");

      navigate(ROUTES.LESSONS);
    } finally {
      setLoading(false);
    }
  }

  async function loadCategories() {
    try {
      const data = await getCategories();

      if (lesson) {
        const cat = data.find(
          (c) => c.id === lesson.category_id,
        );

        setCategory(cat);
      }
    } catch (err) {
      console.error(
        "Failed to load categories:",
        err,
      );
    }
  }

  useEffect(() => {
    if (lesson) {
      loadCategories();
    }
  }, [lesson]);

  async function handleDelete() {
    confirmDelete(
      `lesson "${lesson.case}"`,
      async () => {
        try {
          await deleteLesson(id);

          showSuccess(
            "Lesson berhasil dihapus",
          );

          navigate(ROUTES.LESSONS);
        } catch (err) {
          console.error(
            "Failed to delete lesson:",
            err,
          );

          if (
            err.response?.status === 403
          ) {
            showError(
              "Anda hanya bisa menghapus lesson yang Anda buat sendiri",
            );
          } else {
            showError(
              "Gagal menghapus lesson",
            );
          }
        }
      },
    );
  }

  async function handleApprove() {
    showConfirm({
      title: "Setujui Lesson?",
      message:
        "Lesson ini akan disetujui dan muncul di dashboard publik",
      confirmLabel: "Ya, Setujui",
      cancelLabel: "Batal",
      confirmColor: "blue",

      onConfirm: async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/lessons/${id}/approve`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  JWT.TOKEN_KEY,
                )}`,
              },
            },
          );

          if (!response.ok) {
            const errorData =
              await response.json();

            throw new Error(
              errorData.error ||
                "Failed to approve lesson",
            );
          }

          showSuccess(
            "Lesson berhasil disetujui",
          );

          loadLesson();
        } catch (err) {
          console.error(
            "Failed to approve lesson:",
            err,
          );

          showError(
            err.message ||
              "Gagal approve lesson",
          );
        }
      },
    });
  }

  async function handleUnapprove() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/lessons/${id}/unapprove`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              JWT.TOKEN_KEY,
            )}`,
          },
        },
      );

      if (!response.ok) {
        const errorData =
          await response.json();

        throw new Error(
          errorData.error ||
            "Failed to unapprove lesson",
        );
      }

      showSuccess(
        "Lesson berhasil di-unapprove",
      );

      loadLesson();
    } catch (err) {
      console.error(
        "Failed to unapprove lesson:",
        err,
      );

      showError(
        err.message ||
          "Gagal unapprove lesson",
      );
    }
  }

  if (loading || !lesson) {
    return (
      <LoadingOverlay isLoading={true} />
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-white">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-120px] left-[-80px] w-[380px] h-[380px] bg-cyan-200/30 rounded-full blur-3xl animate-pulse" />

        <div
          className="absolute bottom-[-120px] right-[-80px] w-[420px] h-[420px] bg-blue-200/30 rounded-full blur-3xl animate-pulse"
          style={{
            animationDuration: "6s",
          }}
        />

        <div
          className="absolute top-[45%] left-[40%] w-[260px] h-[260px] bg-sky-200/20 rounded-full blur-3xl animate-pulse"
          style={{
            animationDuration: "8s",
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-6 pt-24">
        {/* Header */}
        <div className="mb-8">
          {/* Back */}
          <button
            onClick={() =>
              navigate(ROUTES.LESSONS)
            }
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-2xl border border-gray-200 bg-white/70 backdrop-blur-xl shadow-sm hover:shadow-md text-sm text-gray-600 hover:text-gray-900 transition-all duration-300 mb-6"
          >
            <Undo2
              size={15}
              className="group-hover:-translate-x-1 transition-transform"
            />

            <span>
              Kembali ke List Lessons
            </span>
          </button>

          {/* Title Card */}
          <div className="relative overflow-hidden rounded-[28px] border border-gray-200 bg-white/70 backdrop-blur-2xl shadow-sm p-6 md:p-8">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/40 to-blue-50/20" />

            <div className="relative z-10 flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-100 bg-cyan-50 mb-4">
                  <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />

                  <span className="text-[11px] uppercase tracking-widest text-cyan-700">
                    Lesson Documentation
                  </span>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <PageTitle>
                    {lesson.case || "-"}
                  </PageTitle>

                  <StatusToggle
                    status={lesson.status}
                    canEdit={canApprove}
                    onApprove={
                      handleApprove
                    }
                    onUnapprove={
                      handleUnapprove
                    }
                  />
                </div>

              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <EditButton
                  onClick={() =>
                    navigate(
                      ROUTES.LESSONS_EDIT(
                        id,
                      ),
                    )
                  }
                  size={20}
                />

                <DeleteButton
                  onClick={handleDelete}
                  size={20}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="relative overflow-hidden rounded-[28px] border border-gray-200 bg-white/70 backdrop-blur-2xl shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 text-sm text-gray-600">
            <div className="flex items-start gap-3">
              <Calendar
                size={16}
                className="mt-0.5 text-cyan-600"
              />

              <div>
                <p className="text-xs text-gray-400">
                  Dibuat
                </p>

                <p className="font-medium text-gray-700">
                  {formatDateShort(
                    lesson.created_at,
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User
                size={16}
                className="mt-0.5 text-cyan-600"
              />

              <div>
                <p className="text-xs text-gray-400">
                  Dibuat Oleh
                </p>

                <p className="font-medium text-gray-700">
                  {lesson.user_name ||
                    "Unknown"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <List
                size={16}
                className="mt-0.5 text-cyan-600"
              />

              <div>
                <p className="text-xs text-gray-400">
                  Kategori
                </p>

                <p className="font-medium text-gray-700">
                  {category?.name || "-"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FolderOpen
                size={16}
                className="mt-0.5 text-cyan-600"
              />

              <div>
                <p className="text-xs text-gray-400">
                  Proyek
                </p>

                <p className="font-medium text-gray-700">
                  {lesson.project_name ||
                    "-"}

                  {lesson.project_division && (
                    <span className="text-gray-400 ml-1">
                      (
                      {
                        lesson.project_division
                      }
                      )
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative overflow-hidden rounded-[28px] border border-gray-200 bg-white/70 backdrop-blur-2xl shadow-sm p-6 md:p-8 mb-8">
          {/* Penyebab */}
          <div className="mb-8">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              Penyebab / Root Cause
            </h3>

            <p className="text-sm md:text-[15px] leading-7 text-gray-600 whitespace-pre-wrap">
              {lesson.causes}
            </p>
          </div>

          {/* Dampak */}
          <div className="mb-8">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-orange-500" />
              Dampak / Impact
            </h3>

            <p className="text-sm md:text-[15px] leading-7 text-gray-600 whitespace-pre-wrap">
              {lesson.effect}
            </p>
          </div>

          {/* Tindakan */}
          <div className="mb-8">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-cyan-500" />
              Tindakan yang Dilakukan
            </h3>

            <p className="text-sm md:text-[15px] leading-7 text-gray-600 whitespace-pre-wrap">
              {lesson.project_action}
            </p>
          </div>

          {/* Rekomendasi */}
          <div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              Rekomendasi untuk Masa
              Depan
            </h3>

            <p className="text-sm md:text-[15px] leading-7 text-gray-600 whitespace-pre-wrap">
              {
                lesson.future_recommendation
              }
            </p>
          </div>
        </div>

        {/* Gallery */}
        {lesson.photos &&
          lesson.photos.length > 0 && (
            <div className="relative overflow-hidden rounded-[28px] border border-gray-200 bg-white/70 backdrop-blur-2xl shadow-sm p-6 md:p-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Foto Dokumentasi (
                {lesson.photos.length})
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {lesson.photos.map(
                  (photo, index) => (
                    <div
                      key={index}
                      className="relative group cursor-pointer overflow-hidden rounded-2xl"
                      onClick={() =>
                        setSelectedPhoto(
                          `${API_BASE_URL}/${photo}`,
                        )
                      }
                    >
                      <img
                        src={`${API_BASE_URL}/${photo}`}
                        alt={`Dokumentasi ${
                          index + 1
                        }`}
                        className="w-full h-52 object-cover rounded-2xl border border-gray-200 group-hover:scale-105 group-hover:border-cyan-400 transition-all duration-500 shadow-sm"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end justify-start p-4">
                        <span className="text-white text-xs font-medium tracking-wide">
                          Klik untuk
                          memperbesar
                        </span>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}

        {/* Modal */}
        {selectedPhoto && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() =>
              setSelectedPhoto(null)
            }
          >
            <div className="relative max-w-5xl max-h-[90vh]">
              <img
                src={selectedPhoto}
                alt="Preview"
                className="max-w-full max-h-[90vh] object-contain rounded-3xl shadow-2xl"
              />

              <button
                onClick={() =>
                  setSelectedPhoto(null)
                }
                className="absolute top-4 right-4 h-10 px-4 rounded-2xl bg-white/90 backdrop-blur-xl text-sm text-gray-800 hover:bg-white transition-all shadow-lg"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
